import { useEffect, useRef, useState } from 'react';

import type {
  AvatarAnimation,
  AvatarState,
  AvatarStateController,
  UseAvatarStateParams,
} from './avatarTypes';

const ANIMATION_RESET_MS = 1100;

function pickRandom<T>(options: readonly T[]): T {
  return options[Math.floor(Math.random() * options.length)] ?? options[0];
}

function randomInterval(minMs: number, maxMs: number) {
  return minMs + Math.random() * (maxMs - minMs);
}

function getStaticState(params: UseAvatarStateParams): AvatarState {
  if (params.hasError) {
    return 'concerned';
  }

  if (!params.isChatOpen) {
    return 'neutral';
  }

  if (params.isAssistantThinking) {
    return 'thinking';
  }

  if (params.isUserTyping) {
    return 'curious';
  }

  return 'neutral';
}

function useResolvedReducedMotion(explicitReducedMotion?: boolean) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof explicitReducedMotion === 'boolean') {
      setPrefersReducedMotion(explicitReducedMotion);
      return;
    }

    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(query.matches);

    update();
    query.addEventListener?.('change', update);

    return () => query.removeEventListener?.('change', update);
  }, [explicitReducedMotion]);

  return explicitReducedMotion ?? prefersReducedMotion;
}

export function useAvatarState(params: UseAvatarStateParams): AvatarStateController {
  const {
    hasError,
    hasNewResponse,
    isAssistantThinking,
    isChatOpen,
    isUserTyping,
    reducedMotion,
  } = params;
  const resolvedReducedMotion = useResolvedReducedMotion(reducedMotion);
  const [state, setState] = useState<AvatarState>('neutral');
  const [animation, setAnimation] = useState<AvatarAnimation | null>(null);
  const [idleGlow, setIdleGlow] = useState(!resolvedReducedMotion);
  const [tilt, setTilt] = useState(false);
  const previousOpenRef = useRef(isChatOpen);
  const timersRef = useRef<number[]>([]);
  const animationClearTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (resolvedReducedMotion) {
      setIdleGlow(false);
      setTilt(false);
    } else if (!isChatOpen) {
      setIdleGlow(true);
    }
  }, [isChatOpen, resolvedReducedMotion]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current = [];

      if (animationClearTimerRef.current !== null) {
        window.clearTimeout(animationClearTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    function clearBehaviorTimers() {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current = [];
    }

    function schedule(callback: () => void, delay: number) {
      const timer = window.setTimeout(() => {
        timersRef.current = timersRef.current.filter((candidate) => candidate !== timer);
        callback();
      }, delay);

      timersRef.current.push(timer);
      return timer;
    }

    function trigger(nextAnimation: AvatarAnimation | null) {
      if (resolvedReducedMotion || !nextAnimation) {
        setAnimation(null);
        return;
      }

      if (animationClearTimerRef.current !== null) {
        window.clearTimeout(animationClearTimerRef.current);
      }

      setAnimation(nextAnimation);
      animationClearTimerRef.current = window.setTimeout(() => {
        setAnimation(null);
        animationClearTimerRef.current = null;
      }, ANIMATION_RESET_MS);
    }

    function scheduleClosedIdleReminder() {
      schedule(() => {
        const behavior = pickRandom(['bounce', 'glow', 'tilt'] as const);

        if (behavior === 'bounce') {
          trigger('bounce');
        } else if (behavior === 'glow') {
          setIdleGlow(false);
          schedule(() => setIdleGlow(true), 220);
        } else {
          setTilt(true);
          schedule(() => setTilt(false), 3600);
        }

        scheduleClosedIdleReminder();
      }, randomInterval(60_000, 120_000));
    }

    function scheduleTypingBlink() {
      schedule(() => {
        trigger('blink');
        scheduleTypingBlink();
      }, randomInterval(3_000, 5_000));
    }

    function scheduleThinkingCycle() {
      schedule(() => {
        trigger(pickRandom([null, 'think', 'slowblink'] as Array<AvatarAnimation | null>));
        scheduleThinkingCycle();
      }, randomInterval(2_000, 3_000));
    }

    function scheduleOpenIdleBlink() {
      schedule(() => {
        trigger('blink');
        scheduleOpenIdleBlink();
      }, randomInterval(5_000, 8_000));
    }

    function scheduleOpenIdleExpression() {
      schedule(() => {
        setState((current) => (current === 'curious' ? 'neutral' : 'curious'));
        scheduleOpenIdleExpression();
      }, randomInterval(20_000, 30_000));
    }

    function scheduleOpenIdle() {
      scheduleOpenIdleBlink();
      scheduleOpenIdleExpression();
    }

    clearBehaviorTimers();

    if (resolvedReducedMotion) {
      setAnimation(null);
      setIdleGlow(false);
      setTilt(false);
      setState(
        getStaticState({
          hasError,
          hasNewResponse,
          isAssistantThinking,
          isChatOpen,
          isUserTyping,
          reducedMotion,
        }),
      );
      previousOpenRef.current = isChatOpen;
      return;
    }

    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = isChatOpen;

    if (hasError) {
      setState('concerned');
      setIdleGlow(false);
      setTilt(false);
      trigger('slowblink');
      return;
    }

    if (hasNewResponse && isChatOpen) {
      setState(pickRandom(['happy', 'surprised'] as const));
      setIdleGlow(true);
      setTilt(false);
      trigger('bounce');
      schedule(() => trigger('widen'), 340);
      schedule(() => {
        setState('neutral');
        setIdleGlow(false);
        scheduleOpenIdle();
      }, randomInterval(2_000, 3_000));
      return;
    }

    if (!wasOpen && isChatOpen) {
      setState(pickRandom(['happy', 'surprised'] as const));
      setIdleGlow(true);
      setTilt(false);
      trigger('bounce');
      schedule(() => {
        setState('neutral');
        setIdleGlow(false);
        scheduleOpenIdle();
      }, randomInterval(1_500, 2_000));
      return;
    }

    if (wasOpen && !isChatOpen) {
      setState(pickRandom(['concerned', 'surprised'] as const));
      setIdleGlow(false);
      setTilt(false);
      trigger('blink');
      schedule(() => {
        setState('neutral');
        setIdleGlow(true);
        scheduleClosedIdleReminder();
      }, 2_000);
      return;
    }

    if (!isChatOpen) {
      setState('neutral');
      setIdleGlow(true);
      setTilt(false);
      scheduleClosedIdleReminder();
      return;
    }

    if (isAssistantThinking) {
      setState('thinking');
      setIdleGlow(false);
      setTilt(false);
      scheduleThinkingCycle();
      return;
    }

    if (isUserTyping) {
      setState('curious');
      setIdleGlow(false);
      setTilt(false);
      scheduleTypingBlink();
      return;
    }

    setState('neutral');
    setIdleGlow(false);
    setTilt(false);
    scheduleOpenIdle();
  }, [
    hasError,
    hasNewResponse,
    isAssistantThinking,
    isChatOpen,
    isUserTyping,
    reducedMotion,
    resolvedReducedMotion,
  ]);

  return {
    animation,
    idleGlow,
    state,
    tilt,
  };
}
